import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import axios from 'axios';
import { useToast } from '../../../contexts/ToastContext';
import RaiseEdrWorkOrderDialog from './RaiseEdrWorkOrderDialog';

function formatUser(u) {
  if (!u) return '—';
  const n = [u.firstName, u.lastName].filter(Boolean).join(' ').trim();
  return n || '—';
}

function formatWhen(iso) {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(iso));
  } catch {
    return '—';
  }
}

function formatCycles(c) {
  if (!c || typeof c !== 'object') return '—';
  const parts = Object.entries(c)
    .filter(([, v]) => v != null && String(v).trim() !== '')
    .map(([k, v]) => `${k}: ${v}`);
  return parts.length ? parts.join(', ') : '—';
}

const FORM_FIELD_ROWS = [
  ['FORM No.', 'formNumber'],
  ['EDR reference', 'edrReferenceNumber'],
  ['Reference date', 'referenceDate'],
  ['Aircraft (type / model)', 'aircraftType'],
  ['Registration', 'aircraftRegistration'],
  ['Aircraft hours', 'aircraftHours'],
  ['Aircraft landings', 'aircraftLandings'],
  ['Engine serial no. (No.1)', 'engine1Serial'],
  ['Engine serial no. (No.2)', 'engine2Serial'],
  ['Engine hours (No.1)', 'engine1Hours'],
  ['Engine hours (No.2)', 'engine2Hours'],
  ['Defect date / station', 'defectDateStation'],
  ['Authorisation number', 'icplNumber'],
  ['Work order ref.', 'workOrderRefNo'],
  ['Snag reported by', 'snagReportedBy'],
  ['Defect details', 'defectDetails'],
  ['Affected parts', 'affectedPartsDetails'],
  ['Part number', 'partNumber'],
  ['Serial number', 'serialNumber'],
  ['AME man-hours', 'ameManHours'],
  ['Tech man-hours', 'techManHours'],
  ['Proposed rectification', 'proposedRectification'],
  ['EDR sent / distribution note', 'edrSentNote'],
  ['Raising person', 'raisingPersonName'],
  ['Designation', 'designation'],
  ['Signature note', 'signatureNote'],
  ['Raising date', 'raisingDate'],
  ['Place', 'raisingPlace']
];

/**
 * CAMO inbox: Engineering Defect Reports sent from MRO (Send to CAMO).
 */
const EdrInboxTab = ({ onOpenWorkOrder, aircraft = [], displayTimezone, onWorkOrderCreated }) => {
  const { showToast } = useToast();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewRow, setViewRow] = useState(null);
  const [createEdrRow, setCreateEdrRow] = useState(null);

  const fetchInbox = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/maintenance/edr/inbox');
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      showToast(e.response?.data?.message || 'Failed to load EDR inbox', 'error');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    fetchInbox();
  }, [fetchInbox]);

  const openCreateDialog = (row) => {
    if (!row || row.edrWorkOrderId) return;
    setCreateEdrRow(row);
  };

  const handleWorkOrderCreated = async (workOrder) => {
    await fetchInbox();
    setViewRow(null);
    if (onWorkOrderCreated) {
      onWorkOrderCreated(workOrder);
    }
  };

  const f = viewRow?.form || {};

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} sx={{ mb: 2 }} spacing={1}>
        <Box>
          <Typography variant="h6">EDR inbox</Typography>
          <Typography variant="body2" color="text.secondary">
            Engineering Defect Reports sent to CAMO from the MRO workbench. Create work orders from EDR details (numbered parent WO + a, b, c…); they appear in the main work order list.
          </Typography>
        </Box>
        <Button variant="outlined" size="small" onClick={fetchInbox} disabled={loading}>
          Refresh
        </Button>
      </Stack>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Sent</TableCell>
              <TableCell>EDR reference</TableCell>
              <TableCell>Work order</TableCell>
              <TableCell>Aircraft</TableCell>
              <TableCell>Authorisation number</TableCell>
              <TableCell>Raised by</TableCell>
              <TableCell>Sent by (MRO)</TableCell>
              <TableCell>EDR WO</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    Loading…
                  </Typography>
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    No EDRs have been sent to CAMO yet.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row, idx) => {
                const key = `${row.workOrderId}-${row.edrIndex ?? row.sentAt}-${idx}`;
                const raisedBy =
                  row.raisedBy
                  || row.raisingPersonName
                  || row.form?.raisingPersonName
                  || row.snagReportedBy
                  || row.form?.snagReportedBy
                  || '';
                return (
                  <TableRow key={key} hover>
                    <TableCell>{formatWhen(row.sentAt)}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {row.edrReferenceNumber || row.form?.edrReferenceNumber || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {row.workOrderNumber || String(row.workOrderId).slice(-8)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {row.title || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>{row.aircraftRegistration || '—'}</TableCell>
                    <TableCell>{row.icplNumber || '—'}</TableCell>
                    <TableCell>{raisedBy || '—'}</TableCell>
                    <TableCell>{formatUser(row.sentBy)}</TableCell>
                    <TableCell>
                      {row.edrWorkOrderId ? (
                        <Stack spacing={0.25}>
                          <Typography variant="body2" fontWeight={500}>
                            {row.edrWorkOrderNumber || '—'}
                          </Typography>
                          {row.edrWorkOrderStatus && (
                            <Chip size="small" label={row.edrWorkOrderStatus} variant="outlined" sx={{ width: 'fit-content' }} />
                          )}
                        </Stack>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          —
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end" flexWrap="wrap">
                        <Button size="small" onClick={() => setViewRow(row)}>
                          View EDR
                        </Button>
                        <Button size="small" variant="outlined" onClick={() => onOpenWorkOrder({ _id: row.workOrderId })}>
                          Parent WO
                        </Button>
                        {row.edrWorkOrderId ? (
                          <Button
                            size="small"
                            variant="contained"
                            color="primary"
                            onClick={() => onOpenWorkOrder({ _id: row.edrWorkOrderId })}
                          >
                            Open EDR WO
                          </Button>
                        ) : (
                          <Button
                            size="small"
                            variant="contained"
                            color="secondary"
                            onClick={() => openCreateDialog(row)}
                          >
                            Create work order
                          </Button>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={Boolean(viewRow)} onClose={() => setViewRow(null)} maxWidth="md" fullWidth scroll="paper">
        <DialogTitle>Engineering Defect Report</DialogTitle>
        <DialogContent dividers>
          {viewRow && (
            <Stack spacing={2}>
              <Stack direction="row" flexWrap="wrap" gap={2}>
                <Typography variant="body2">
                  <strong>Sent:</strong> {formatWhen(viewRow.sentAt)}
                </Typography>
                <Typography variant="body2">
                  <strong>EDR reference:</strong> {viewRow.edrReferenceNumber || f.edrReferenceNumber || '—'}
                </Typography>
                <Typography variant="body2">
                  <strong>Authorisation number:</strong> {viewRow.icplNumber || '—'}
                </Typography>
                <Typography variant="body2">
                  <strong>Sent by:</strong> {formatUser(viewRow.sentBy)}
                </Typography>
                <Typography variant="body2">
                  <strong>Raised by:</strong>{' '}
                  {viewRow.raisedBy
                    || viewRow.raisingPersonName
                    || f.raisingPersonName
                    || viewRow.snagReportedBy
                    || f.snagReportedBy
                    || '—'}
                </Typography>
                <Typography variant="body2">
                  <strong>Parent work order:</strong> {viewRow.workOrderNumber || String(viewRow.workOrderId)}
                </Typography>
                {viewRow.edrWorkOrderNumber && (
                  <Typography variant="body2">
                    <strong>EDR work order:</strong> {viewRow.edrWorkOrderNumber}
                    {viewRow.edrWorkOrderStatus ? ` (${viewRow.edrWorkOrderStatus})` : ''}
                  </Typography>
                )}
              </Stack>
              <Typography variant="subtitle2" color="primary">
                Form
              </Typography>
              <Table size="small">
                <TableBody>
                  {FORM_FIELD_ROWS.map(([label, key]) => (
                    <TableRow key={key}>
                      <TableCell sx={{ width: '32%', fontWeight: 500, verticalAlign: 'top' }}>{label}</TableCell>
                      <TableCell sx={{ whiteSpace: 'pre-wrap' }}>{f[key] != null && f[key] !== '' ? String(f[key]) : '—'}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell sx={{ fontWeight: 500, verticalAlign: 'top' }}>Engine cycles — No.1</TableCell>
                    <TableCell sx={{ whiteSpace: 'pre-wrap' }}>{formatCycles(f.engine1Cycles)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 500, verticalAlign: 'top' }}>Engine cycles — No.2</TableCell>
                    <TableCell sx={{ whiteSpace: 'pre-wrap' }}>{formatCycles(f.engine2Cycles)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          {viewRow && (
            <>
              <Button onClick={() => onOpenWorkOrder({ _id: viewRow.workOrderId })}>Open parent WO</Button>
              {viewRow.edrWorkOrderId ? (
                <Button variant="contained" onClick={() => onOpenWorkOrder({ _id: viewRow.edrWorkOrderId })}>
                  Open EDR WO
                </Button>
              ) : (
                <Button variant="contained" color="secondary" onClick={() => openCreateDialog(viewRow)}>
                  Create work order
                </Button>
              )}
            </>
          )}
          <Button onClick={() => setViewRow(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <RaiseEdrWorkOrderDialog
        open={Boolean(createEdrRow)}
        onClose={() => setCreateEdrRow(null)}
        edrRow={createEdrRow}
        aircraft={aircraft}
        displayTimezone={displayTimezone}
        onCreated={handleWorkOrderCreated}
      />
    </Box>
  );
};

export default EdrInboxTab;
